import React, { useState, useRef, useEffect } from "react";
import "./VideoPlayer.css";

const VideoPlayer = ({ 
  videoSrc, 
  posterSrc, 
  width = "100%", 
  height = "335px",
  controls = true,
  loop = false,
  autoplay = false,
  className = "",
  onFullscreenChange = null
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const videoRef = useRef(null);
  const videoContainerRef = useRef(null);
  const controlsTimeoutRef = useRef(null);

  // Função para iniciar reprodução infinita
  const startInfinitePlay = async () => {
    if (!videoRef.current) return;
    
    videoRef.current.play();
    setIsPlaying(true);
    setShowControls(true);
    resetControlsTimeout();
    
    // Entrar em fullscreen automaticamente
    await toggleFullscreen();
  };

  // Função para pausar/retomar o vídeo
  const togglePlayPause = (e) => {
    e.stopPropagation();
    if (!videoRef.current) return;
    
    if (isPlaying) {
      videoRef.current.pause();
      setIsPlaying(false);
    } else {
      videoRef.current.play();
      setIsPlaying(true);
    }
    setShowControls(true);
    resetControlsTimeout();
  };

  // Função para controlar volume (slider)
  const handleVolumeSliderChange = (e) => {
    e.stopPropagation();
    if (!videoRef.current) return;
    
    const newVolume = parseFloat(e.target.value);
    videoRef.current.volume = newVolume;
    videoRef.current.muted = newVolume === 0;
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
    setShowControls(true);
    resetControlsTimeout();
  };

  // Função para alternar mute
  const toggleMute = (e) => {
    e.stopPropagation();
    if (!videoRef.current) return;
    
    videoRef.current.muted = !videoRef.current.muted;
    setIsMuted(videoRef.current.muted);
    setShowControls(true);
    resetControlsTimeout();
  };

  // Função para resetar timeout dos controles
  const resetControlsTimeout = () => {
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    controlsTimeoutRef.current = setTimeout(() => {
      setShowControls(false);
    }, 3000);
  };

  // Mostrar controles ao passar o mouse
  const handleMouseEnter = () => {
    if (isPlaying) {
      setShowControls(true);
      resetControlsTimeout();
    }
  };

  const handleMouseMove = () => {
    if (isPlaying) {
      setShowControls(true);
      resetControlsTimeout();
    }
  };

  const handleMouseLeave = () => {
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    setShowControls(false);
  };

  const toggleFullscreen = async () => {
    if (!videoRef.current) return;

    try {
      setIsTransitioning(true);
      
      if (!isFullscreen) {
        // Entrar em fullscreen
        if (videoRef.current.requestFullscreen) {
          await videoRef.current.requestFullscreen();
        } else if (videoRef.current.webkitRequestFullscreen) {
          await videoRef.current.webkitRequestFullscreen();
        } else if (videoRef.current.msRequestFullscreen) {
          await videoRef.current.msRequestFullscreen();
        }
        setIsFullscreen(true);
        if (onFullscreenChange) onFullscreenChange(true);
      } else {
        // Sair do fullscreen
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          await document.webkitExitFullscreen();
        } else if (document.msExitFullscreen) {
          await document.msExitFullscreen();
        }
        setIsFullscreen(false);
        if (onFullscreenChange) onFullscreenChange(false);
      }
      
      // Aguardar um pouco para a transição completar
      setTimeout(() => {
        setIsTransitioning(false);
      }, 500);
    } catch (error) {
      console.error('Erro ao alternar fullscreen:', error);
      setIsTransitioning(false);
    }
  };

  // Listener para detectar mudanças no fullscreen
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isCurrentlyFullscreen = !!(
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.msFullscreenElement
      );
      setIsFullscreen(isCurrentlyFullscreen);
      if (onFullscreenChange) onFullscreenChange(isCurrentlyFullscreen);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('msfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('msfullscreenchange', handleFullscreenChange);
    };
  }, [onFullscreenChange]);

  // Listener para eventos do vídeo
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handlePlay = () => {
      setIsPlaying(true);
    };

    const handlePause = () => {
      setIsPlaying(false);
    };

    const handleEnded = () => {
      // Reinicia o vídeo quando termina
      video.currentTime = 0;
      video.play();
    };

    const handleTimeUpdate = () => {
      setCurrentTime(video.currentTime);
    };

    const handleLoadedMetadata = () => {
      setDuration(video.duration);
      // Sincronizar volume inicial
      setVolume(video.volume);
      setIsMuted(video.muted);
    };

    const handleVolumeChange = () => {
      setVolume(video.volume);
      setIsMuted(video.muted);
    };

    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('ended', handleEnded);
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.addEventListener('volumechange', handleVolumeChange);

    return () => {
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.removeEventListener('volumechange', handleVolumeChange);
    };
  }, [isPlaying]);

  // Limpar timeout ao desmontar
  useEffect(() => {
    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div 
      ref={videoContainerRef} 
      className={`video-player-container ${className} ${isPlaying ? 'playing' : ''} ${isTransitioning ? 'transitioning' : ''}`}
      style={{ width, height }}
      onMouseEnter={handleMouseEnter}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <video 
        ref={videoRef}
        width="100%" 
        height="100%"
        controls={false}
        loop={loop || isPlaying}
        autoPlay={autoplay}
        poster={posterSrc}
        className="video-player"
        onClick={isPlaying ? toggleFullscreen : startInfinitePlay}
        onContextMenu={(e) => e.preventDefault()}
      >
        <source 
          src={videoSrc} 
          type="video/mp4" 
        />
        Seu navegador não suporta o elemento de vídeo.
      </video>
      {!isPlaying && (
        <div 
          className="video-player-overlay"
          onClick={startInfinitePlay}
        >
          <div className="video-play-button">
            <i className="fa fa-play"></i>
          </div>
        </div>
      )}
      
      {isPlaying && (
        <>
          <div className={`video-controls ${showControls ? 'show' : ''}`}>
            <button 
              className="video-control-btn video-pause-btn"
              onClick={togglePlayPause}
              aria-label={isPlaying ? "Pausar" : "Reproduzir"}
            >
              <i className={`fa ${isPlaying ? 'fa-pause' : 'fa-play'}`}></i>
            </button>
            <div className="video-volume-control">
              <button 
                className="video-control-btn video-mute-btn"
                onClick={toggleMute}
                aria-label={isMuted ? "Ativar som" : "Silenciar"}
              >
                <i className={`fa ${isMuted || volume === 0 ? 'fa-volume-mute' : volume < 0.5 ? 'fa-volume-down' : 'fa-volume-up'}`}></i>
              </button>
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={isMuted ? 0 : volume}
                onChange={handleVolumeSliderChange}
                className="video-volume-slider"
                aria-label="Volume"
              />
            </div>
          </div>
          <div className="video-progress-container">
            <div className="video-progress-bar">
              <div 
                className="video-progress-fill"
                style={{ 
                  width: duration > 0 ? `${(currentTime / duration) * 100}%` : '0%' 
                }}
              ></div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default VideoPlayer;
